/**
 * 法律文本（用户协议 / 隐私政策）—— **文案唯一来源**（N 域 N5 · `AC-N5.x`）
 *
 * 依据：N 域 PRD **§5.5**（信任与合规：6 条承诺 + 精度承诺 + **协议与隐私政策进 P0**）·
 * 规格 §20 · `DR-020`。设置页 `PENDING` 清单里长期挂着「协议页与隐私政策页」⇒ 本批闭合。
 *
 * ## 🔴 写作铁律（每一条都是"写了做不到 = 比不写更糟"）
 *
 * | 铁律 | 本文件怎么做的 |
 * |---|---|
 * | **① 每条陈述必须能被产品行为验证** | 「导出免费」⇒ 已实现（N4）；「不向第三方共享账务数据」⇒ 汇率只发币种代码（`rates` 域实测）；「注销 7 天冷静期」⇒ `DELETION_COOLING_DAYS = 7` |
 * | **② 没做的功能不许写成承诺** | AI 能力（E 域）**尚未上线** ⇒ 隐私政策写「**当前版本未启用**」，而不是"我们会如何保护 AI 输入" |
 * | **③ 不写"我们绝不……"这类无法证伪的绝对句** | 一律写成"**不会**做 X（因为 Y）"，且 X 必须是**当前架构下不可能发生**的事（无广告 SDK / 无第三方统计 / 不采集通讯录） |
 * | **④ 版本与生效日期必须是真值** | `LEGAL_VERSION` / `LEGAL_EFFECTIVE_DATE` 单一来源，页脚直出 |
 *
 * ## ⚠️ 本文件**不是法务意见**
 *
 * 文本由开发侧按产品实际能力起草，**逐条对应可验证的行为**；正式对外前仍需负责人（或法务）过一遍。
 * 这条纪律已写进 changelog 与本文件 —— 🔴 **改文案时请同步改这里的"兑现点"注释**，
 * 否则下一个读的人无法判断某句还能不能成立。
 */

export const LEGAL_VERSION = 'v1.0';
export const LEGAL_EFFECTIVE_DATE = '2026-10-20';

/** 一节：标题 + 段落（段落里可用 `**加粗**` 之外的纯文本；页面按段渲染） */
export interface LegalSection {
  heading: string;
  paragraphs: readonly string[];
}

export const APP_NAME = '家盈';
export const LEGAL_CONTACT = 'badbrain@qq.com';

// ═══════════════════════════ 用户协议 ═══════════════════════════

export const TERMS_TITLE = '用户协议';

/**
 * 用户协议（8 节）。
 *
 * 🔴 兑现点备注（改文案前先读）：
 * - §4「导出」→ N4 已实现（CSV / JSON，不限次数）· §6「维护提前告知」→ 目前无 SLA 承诺，只写"尽力"
 */
export const TERMS_SECTIONS: readonly LegalSection[] = [
  {
    heading: '1. 这是什么服务',
    paragraphs: [
      `${APP_NAME}是一个家庭记账工具：记录收支、管理账户、看净资产与报表。`,
      `${APP_NAME}不是金融机构，不代管任何资金，也不提供投资、税务或法律建议。你在本服务中看到的任何数字，都是你录入（或导入）的数据经过公开规则计算的结果。`,
    ],
  },
  {
    heading: '2. 账号与账本',
    paragraphs: [
      '一个账号可以参与一个或多个账本。账本的数据属于**这个家庭**，不属于某一个成员 —— 加入后所有成员都能看到账本里的全部记录。',
      '账本所有者（owner）可以邀请、移除成员，转让所有权，或解散账本。这些动作会在界面上明确提示后果，并在服务端做二次校验（例如解散需要再次输入账本名称）。',
    ],
  },
  {
    heading: '3. 你可以怎么用，不可以怎么用',
    paragraphs: [
      '你可以用它记录自己与家庭的真实收支。',
      '你不可以：用本服务从事违法活动；尝试绕过权限访问他人账本；对服务进行攻击、扫描或压力测试；冒用他人身份注册或邀请。',
      '出现上述情况时，我们可能限制或终止相关账号的使用。',
    ],
  },
  {
    heading: '4. 数据是你的',
    paragraphs: [
      '你录入的账务数据归你（与你所在的家庭）所有。我们只在你使用本服务所必需的范围内保管与处理它。',
      '你可以随时导出全部数据（流水 CSV 与完整备份 JSON），不限次数、不需要付费；也可以从备份恢复、或申请注销账号。',
      '建议你定期导出一份备份 —— 任何在线服务都不应被当成唯一的存档。',
    ],
  },
  {
    heading: '5. 费用',
    paragraphs: [
      '当前版本的全部功能免费。',
      '如果将来引入收费功能，我们会提前至少 30 天在应用内说明，并保证**已有的导出能力继续免费**。',
    ],
  },
  {
    heading: '6. 服务的变更与中断',
    paragraphs: [
      '我们会尽力保持服务稳定。计划内的维护会尽量避开使用高峰，并在可能时提前告知。',
      '因网络、设备、不可抗力或第三方服务（例如汇率数据源）不可用导致的中断，我们不承担由此产生的间接损失。',
    ],
  },
  {
    heading: '7. 关于数字的边界',
    paragraphs: [
      '本服务用整数「分」做金额运算，并按公开规则聚合（例如净资产、预算、报表）。我们会尽力保证计算正确，但**不保证**它满足你所在地区的会计准则或税务要求。',
      '任何基于本服务的数字做出的财务决定，由你自己判断并承担结果。',
    ],
  },
  {
    heading: '8. 协议的变更与联系',
    paragraphs: [
      '本协议可能随功能变化而更新。重要变更会在应用内提示；继续使用即视为接受更新后的版本。',
      `有疑问可以发邮件到 ${LEGAL_CONTACT}。`,
    ],
  },
];

// ═══════════════════════════ 隐私政策 ═══════════════════════════

export const PRIVACY_TITLE = '隐私政策';

/**
 * 隐私政策（9 节）。
 *
 * 🔴 兑现点备注（改文案前先读）：
 * - §2「无广告 SDK / 无第三方统计」→ 设置页信任承诺第 1 条（`第三方 SDK = 0`）
 * - §5「汇率只发币种代码」→ `modules/rates` 调 Frankfurter，请求体只有币种对与日期
 * - §6「当前未启用 AI」→ E 域尚未实现（`modules/` 里没有 `ai`）
 * - §7「注销 7 天冷静期」→ `DELETION_COOLING_DAYS = 7`（`contract/deletion.ts`）
 */
export const PRIVACY_SECTIONS: readonly LegalSection[] = [
  {
    heading: '1. 我们收集哪些信息',
    paragraphs: [
      '**账号信息**：邮箱、昵称、密码（只保存哈希，不保存明文）。',
      '**账务数据**：你录入或导入的账本、账户、流水、分类、预算、目标等 —— 这些是你主动提供的内容。',
      '**必要的运行信息**：登录时间、请求时间与来源 IP，用于安全审计与故障排查。',
    ],
  },
  {
    heading: '2. 我们不收集哪些信息',
    paragraphs: [
      '不读取通讯录、短信、相册或地理位置。',
      '不接入广告 SDK，也不接入第三方统计分析 SDK（应用内没有任何用于追踪的第三方脚本）。',
      '不做跨站追踪，不出售、不出租你的任何数据。',
    ],
  },
  {
    heading: '3. 这些信息用来做什么',
    paragraphs: [
      '提供你要求的功能（记账、报表、提醒、协作）。',
      '保障账号与数据安全（例如识别异常登录、防止越权访问）。',
      '排查故障与改进产品 —— 只用聚合后的、与个人身份无关的信息。',
    ],
  },
  {
    heading: '4. 存储与保护',
    paragraphs: [
      '数据存储在我们自建的服务器上（不托管给第三方云数据库）。',
      '账号之间、账本之间做严格隔离：每个请求都必须携带账本身份，服务端逐个校验成员资格。',
      '传输使用 HTTPS；密码使用加盐哈希存储。',
    ],
  },
  {
    heading: '5. 会不会提供给第三方',
    paragraphs: [
      '不会向任何第三方出售或共享你的账务数据。',
      '唯一的对外请求是**汇率**：为了把你账户里的外币折算成本币，我们会向公开汇率数据源（Frankfurter）查询币种之间的比率 —— 请求里**只有币种代码与日期**，不包含你的金额、账户名或任何身份信息。',
    ],
  },
  {
    heading: '6. 关于 AI 功能',
    paragraphs: [
      '**当前版本没有启用任何 AI 分析功能**，你的数据不会被发送给任何大模型服务。',
      '如果将来引入 AI 能力，我们会在启用前单独说明：会发送哪些字段、发送给谁、如何关闭；并且默认关闭、逐次征得同意。',
    ],
  },
  {
    heading: '7. 你的权利',
    paragraphs: [
      '**导出**：随时导出全部数据（流水 CSV / 完整备份 JSON），不限次数。',
      '**删除**：可以删除账本内的记录、账户；删除是软删除，30 天内可恢复。',
      '**注销账号**：申请后有 7 天冷静期，期间可撤销；期满后删除账号与相关数据。',
      '**更正**：任何记录都可以在应用内直接修改。',
    ],
  },
  {
    heading: '8. 本地存储',
    paragraphs: [
      '浏览器/小程序本地只保存两类内容：登录凭证（用于免重复登录）与界面偏好缓存（用于避免主题闪烁）。',
      '它们不会被用于追踪，也不包含你的账务明细。',
    ],
  },
  {
    heading: '9. 未成年人、变更与联系',
    paragraphs: [
      '本服务面向家庭场景，未成年人应在监护人指导下使用。',
      '本政策更新时会在应用内提示；重大变更会单独说明。',
      `隐私相关问题可以发邮件到 ${LEGAL_CONTACT}。`,
    ],
  },
];
